import React, { Fragment } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import styled from 'styled-components';
import _split from 'lodash/split';
import _truncate from 'lodash/truncate';
import _orderBy from 'lodash/orderBy';
import _map from 'lodash/map';
import { gqlConflictEnum as CONFLICT } from '@minecraft.graphql-types';
import Icon from '@minecraft.icon';
import Col from '@minecraft.col';
import Row from '@minecraft.row';
import Pill from '@minecraft.pill';
import UncontrolledTooltip from '@minecraft.uncontrolled-tooltip';
import { helpers } from '@minecraft.utils';
import { SCREEN_NAMES, EditProfile, AppType, usePhoneFormat } from '@blocs.common';
import { useTranslation } from '@blocs.i18n';
import { media } from '@minecraft.themes';
import { AnchorButton } from '../styles/ProfileView';
import { useProfileViewContext } from '../ProfileViewContext';

const buttonEditI18Label = 'common:button.edit';
const commonNoSectionI18Label = 'common:form.label.noSelectionYet';

const DuoLink: React.FunctionComponent<{ href: string }> = ({ href, children, ...restProps }) => (
  <Link to={href} {...restProps}>
    {children}
  </Link>
);
/* eslint-disable jsx-a11y/anchor-is-valid */

const sectionName = 'details';

const StyledRow = styled(Row)`
  margin-bottom: 0.5rem;
  border-bottom: 0.0625rem solid ${(props): string => props.theme.colors.primary.ltGrey1};
`;

const StyledColLbl = styled(Col)`
  font-size: 0.85rem;
  font-weight: 600;
  padding-left: 0;
`;

const StyledColValue = styled(({ faded, children, ...props }) => <Col {...props}>{children}</Col>)`
  padding: 0;
  font-size: 0.85rem;
  color: ${(props): string => (props.faded ? props.theme.colors.primary.grey2 : props.theme.colors.primary.darkGrey)};

  ${media.sm`
    padding: 0 0.9375rem;
  `}
`;

const StyledColEditLink = styled(Col)`
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 0;

  ${media.sm`
    padding: 0 0.9375rem;
  `}
`;

const DetailsWrapper = styled.div`
  background: ${(props): string => props.theme.colors.accent.ltGrey2};
  margin: 0 -1rem;
  padding: 1.25rem 2rem;
  ${media.sm`
    margin: 0 -2rem;
  `}
`;

const DetailsContainer = styled.div`
  background: #f9fcff;
  padding: 0.9375rem 3rem;
`;

const RecordsContainer = styled.div`
  padding-left: 1.25rem;
`;

const InfoHeading = styled.h3`
  font-size: 1.7rem;
  line-height: 2rem;
  margin-bottom: 0.5rem;
`;

const StyledBreakString = styled.span`
  word-wrap: break-word;
`;

const StyledPill = styled(Pill)`
  height: auto;
  margin: 0 0.5rem 0.5rem 0;
`;

const StyledAnchor = styled.a`
  margin-left: 0.5rem;
  text-decoration: underline;
`;

const StyledUncontrolledTooltip = styled(UncontrolledTooltip)`
  .tooltip-inner {
    a {
      text-decoration: underline;
      font-size: 0.75rem;
      color: ${(props): string => props.theme.colors.primary.white};
    }
  }
`;

const StyledNoteIcon = styled(Icon)`
  height: 1rem;
  width: 1rem;
  margin-right: 0.5rem;
`;

const Details = (): JSX.Element => {
  const { t, formatIntlDate } = useTranslation();
  const {
    fetcher: { profile, allConflicts, selectedDivision },
    isAdmin,
    onEditClick,
    appType,
    accountCountryCode,
  } = useProfileViewContext();

  const formatPhone = usePhoneFormat(accountCountryCode);

  const [state, setState] = React.useState({
    showFullNote: false,
  });

  const getConflictName = (conflictId) => {
    const foundConflict = allConflicts.find((conflict) => conflict.id.toString() === conflictId?.toString());

    return foundConflict ? foundConflict.name : '';
  };

  const isFutureDate = (date) => moment().startOf('day').isSameOrBefore(date);

  const getReadableDate = (date) => formatIntlDate({ date, format: 'MONTH_DAY_YEAR' });

  const handleSeeMoreClick = () => setState({ showFullNote: true });

  const renderEditOverscaleLink = () => {
    return (
      <StyledColEditLink sm="2" xs="3">
        {isAdmin ? (
          <AnchorButton
            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-permanent-overscale-edit`}
            onClick={() => {
              onEditClick('overscale');
            }}
          >
            {t(buttonEditI18Label)}
          </AnchorButton>
        ) : (
          <DuoLink
            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-permanent-overscale-edit`}
            href={`/profile/${profile.profileId}/overscale`}
          >
            {t(buttonEditI18Label)}
          </DuoLink>
        )}
      </StyledColEditLink>
    );
  };

  const renderNote = (note) => {
    const { showFullNote } = state;
    const noteCharsLength = 400;
    const noteList =
      !showFullNote && note.length > noteCharsLength
        ? _split(_truncate(note, { length: noteCharsLength }), /\n/)
        : _split(note, /\n/);

    return (
      <>
        {noteList.map((noteLine, index) => (
          <StyledBreakString key={helpers.uniqueId('note_')}>
            {noteLine}
            {index < noteList.length - 1 && <br />}
          </StyledBreakString>
        ))}
        {!showFullNote && note.length > noteCharsLength && (
          <StyledAnchor onClick={handleSeeMoreClick}>{t('common:button.seeMore')}</StyledAnchor>
        )}
      </>
    );
  };

  const renderInternalNote = (notes) => {
    const latestNote = _orderBy(notes, ['created'], ['desc'])[0];

    return (
      <div key={latestNote.id}>
        <StyledNoteIcon name={helpers.getNoteIcon(latestNote)} />
        {renderNote(latestNote.comment)}
      </div>
    );
  };

  const renderDefaultSubmissionNote = (note) => renderNote(note);

  const isRepsApp = appType === AppType.Reps;

  const getOrganizationName = () => {
    const talentDivisionName = profile.profileMainDivision?.name
      ? `${profile.profileMainDivision?.parentName} - ${profile.profileMainDivision?.name}`
      : profile.profileMainDivision?.parentName;
    const selectedDivisionCompanyName =
      selectedDivision && selectedDivision.parentOrganization && selectedDivision.parentOrganization.name;
    const selectedDivisionName = isRepsApp
      ? (selectedDivision && selectedDivision.name) || selectedDivisionCompanyName || ''
      : talentDivisionName;

    if (isRepsApp) {
      return selectedDivisionName && selectedDivisionCompanyName
        ? `${selectedDivisionCompanyName} - ${selectedDivisionName}`
        : selectedDivisionName;
    }

    return talentDivisionName;
  };

  const organizationName = getOrganizationName();

  const legacyConflicts = !profile.conflicts
    ? []
    : profile.conflicts.filter((profileConflict) =>
        allConflicts.find(
          (conflict) =>
            Number(conflict.id) === Number(profileConflict.conflictId) && conflict.code === CONFLICT.LEGACY_CONFLICT
        )
      );

  const activeConflicts = !profile.conflicts
    ? []
    : profile.conflicts.filter(
        (profileConflict) =>
          !legacyConflicts.find((legacyConflict) => legacyConflict.conflictId === profileConflict.conflictId) &&
          (isFutureDate(profileConflict.expiration) || !profileConflict.expiration)
      );

  const hasActiveConflicts = activeConflicts.length > 0;
  const hasLegacyConflicts = legacyConflicts.length > 0;
  const hasNotes = profile.notes && profile.notes.length > 0;

  const activeBookOuts = Array.isArray(profile.profileBookOuts)
    ? profile.profileBookOuts.filter((bookOutDate) => isFutureDate(bookOutDate.endDate || bookOutDate.startDate))
    : [];
  const hasBookOuts = activeBookOuts.length > 0;
  const phone = profile?.phone || profile?.contactInfo?.phone;

  return (
    <DetailsWrapper data-testid="profile-details-container">
      <DetailsContainer id="div-details">
        <InfoHeading data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading`}>
          {isRepsApp && !organizationName && t('common:profile.details')}
          {isRepsApp && organizationName && t('common:profile.detailsForOrg', { orgName: organizationName })}
          {!isRepsApp && !organizationName && t('common:profile.myDetails')}
          {!isRepsApp && organizationName && t('common:profile.detailsForOrg', { orgName: organizationName })}
        </InfoHeading>
        <EditProfile.CompoundAppearanceInfo
          data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}`}
          data-testid="profile-visibility-alert"
        >
          {isRepsApp ? t('common:profile.otherRepsOnly') : t('common:profile.repOnly')}
        </EditProfile.CompoundAppearanceInfo>
        <RecordsContainer>
          <StyledRow data-testid="talent-email">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-talent-email`}
              sm="3"
              xs="12"
            >
              {isRepsApp ? `${t('common:form.label.talentEmail')}:` : `${t('common:form.label.contactEmail')}:`}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-email`}
              sm="7"
              xs="9"
              faded={!profile.talentEmail}
            >
              <StyledBreakString>{profile.talentEmail || t(commonNoSectionI18Label)}</StyledBreakString>
            </StyledColValue>
            <StyledColEditLink sm="2" xs="3">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-email-edit`}
                  onClick={() => {
                    onEditClick('talent-email');
                  }}
                >
                  {t(buttonEditI18Label)}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-email-edit`}
                  href={`/profile/${profile.profileId}/contact-email`}
                >
                  {t(buttonEditI18Label)}
                </DuoLink>
              )}
            </StyledColEditLink>
          </StyledRow>
          <StyledRow data-testid="talent-phone">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-talent-phone`}
              sm="3"
              xs="12"
            >
              {isRepsApp ? `${t('common:form.label.talentPhone')}:` : `${t('common:form.label.contactPhone')}:`}
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-phone`}
              sm="7"
              xs="9"
              faded={!phone}
            >
              <StyledBreakString>{formatPhone(phone) || t(commonNoSectionI18Label)}</StyledBreakString>
            </StyledColValue>
            <StyledColEditLink sm="2" xs="3">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-phone-edit`}
                  onClick={() => {
                    onEditClick('talent-phone');
                  }}
                >
                  {t(buttonEditI18Label)}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-talent-phone-edit`}
                  href={`/profile/${profile.profileId}/contact-phone`}
                >
                  {t(buttonEditI18Label)}
                </DuoLink>
              )}
            </StyledColEditLink>
          </StyledRow>
          {isRepsApp && (
            <>
              <StyledRow data-testid="permanent-overscale">
                <Col xs="12">
                  <Row>
                    <StyledColLbl
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-permanent-overscale`}
                      sm="3"
                      xs="12"
                    >
                      {t('common:profile.permanentOverscale')}:
                    </StyledColLbl>
                    <StyledColValue
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-permanent-overscale`}
                      sm={profile.isOverscaled ? '9' : '7'}
                      xs={profile.isOverscaled ? '12' : '9'}
                      faded={!profile.isOverscaled}
                    >
                      {profile.isOverscaled ? t('common:form.label.yes') : t(commonNoSectionI18Label)}
                    </StyledColValue>
                    {!profile.isOverscaled && renderEditOverscaleLink()}
                  </Row>
                  {profile.isOverscaled && (
                    <Row>
                      <StyledColLbl
                        data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-overscale-note`}
                        sm="3"
                        xs="12"
                      >
                        {t('common:profile.overscaleNote')}:
                      </StyledColLbl>
                      <StyledColValue
                        data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-overscale-note`}
                        sm="7"
                        xs="9"
                        faded={!profile.overscaleNote}
                      >
                        <StyledBreakString>{profile.overscaleNote || t(commonNoSectionI18Label)}</StyledBreakString>
                      </StyledColValue>
                      {renderEditOverscaleLink()}
                    </Row>
                  )}
                </Col>
              </StyledRow>
            </>
          )}
          <StyledRow data-testid="manager-email">
            <StyledColLbl
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-manager-email`}
              sm="3"
              xs="12"
            >
              {t('common:profile.managerEmail')}:
            </StyledColLbl>
            <StyledColValue
              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-manager-email`}
              sm="7"
              xs="9"
              faded={!profile.managerEmail}
            >
              <StyledBreakString>{profile.managerEmail || t(commonNoSectionI18Label)}</StyledBreakString>
            </StyledColValue>
            <StyledColEditLink sm="2" xs="3">
              {isAdmin ? (
                <AnchorButton
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-manager-email-edit`}
                  onClick={() => {
                    onEditClick('manager-email');
                  }}
                >
                  {t(buttonEditI18Label)}
                </AnchorButton>
              ) : (
                <DuoLink
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-manager-email-edit`}
                  href={`/profile/${profile.profileId}/manager-email`}
                >
                  {t(buttonEditI18Label)}
                </DuoLink>
              )}
            </StyledColEditLink>
          </StyledRow>
          {isRepsApp && (
            <>
              <StyledRow data-testid="submission-note">
                <StyledColLbl
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-default-submission-note`}
                  sm="3"
                  xs="12"
                >
                  {t('common:profile.defaultSubmissionNote')}:
                </StyledColLbl>
                <StyledColValue
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-default-submission-note`}
                  sm="7"
                  xs="9"
                  faded={!profile.defaultSubmissionNote}
                >
                  {profile.defaultSubmissionNote
                    ? renderDefaultSubmissionNote(profile.defaultSubmissionNote)
                    : t(commonNoSectionI18Label)}
                </StyledColValue>
                <StyledColEditLink sm="2" xs="3">
                  {isAdmin ? (
                    <AnchorButton
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-default-submission-note-edit`}
                      onClick={() => {
                        onEditClick('submission-note');
                      }}
                    >
                      {t(buttonEditI18Label)}
                    </AnchorButton>
                  ) : (
                    <DuoLink
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-default-submission-note-edit`}
                      href={`/profile/${profile.profileId}/submission-note`}
                    >
                      {t(buttonEditI18Label)}
                    </DuoLink>
                  )}
                </StyledColEditLink>
              </StyledRow>
              <StyledRow data-testid="conflicts">
                <StyledColLbl
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-conflicts`}
                  sm="3"
                  xs="12"
                >
                  {t('common:profile.conflicts')}:
                </StyledColLbl>
                <StyledColValue
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-conflicts`}
                  sm="7"
                  xs="9"
                  faded={!hasActiveConflicts}
                >
                  {hasActiveConflicts
                    ? activeConflicts.map((conflict) => (
                        <Fragment key={conflict.profileConflictId}>
                          <StyledPill
                            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-conflict-name`}
                            text={getConflictName(conflict.conflictId)}
                            id={`conflict${conflict.profileConflictId}`}
                            rightIcons={conflict.note ? ['document'] : null}
                          />
                          <StyledUncontrolledTooltip
                            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-conflict-tooltip`}
                            placement="top"
                            autohide={false}
                            target={`conflict${conflict.profileConflictId}`}
                            data-testid={`conflict-tooltip-${conflict.profileConflictId}`}
                          >
                            {conflict.expiration && (
                              <div>
                                {t('common:profile.expireDate', { date: getReadableDate(conflict.expiration) })}
                              </div>
                            )}
                            <DuoLink
                              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-conflict-details`}
                              href={`/profile/${profile.profileId}/conflicts`}
                            >
                              {t('common:profile.viewConflictDetails')}
                            </DuoLink>
                          </StyledUncontrolledTooltip>
                        </Fragment>
                      ))
                    : t(commonNoSectionI18Label)}
                </StyledColValue>
                <StyledColEditLink sm="2" xs="3">
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-conflicts-edit`}
                    href={`/profile/${profile.profileId}/conflicts`}
                  >
                    {t(buttonEditI18Label)}
                  </DuoLink>
                </StyledColEditLink>
              </StyledRow>

              {hasLegacyConflicts && (
                <StyledRow data-testid="legacy-conflicts">
                  <>
                    <StyledColLbl
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-other-conflicts`}
                      sm="3"
                      xs="12"
                    >
                      {t('common:profile.otherConflicts')}
                    </StyledColLbl>
                    <StyledColValue
                      data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-conflicts`}
                      sm="7"
                      xs="9"
                      faded={false}
                    >
                      {legacyConflicts.map((conflict) => (
                        <Fragment key={conflict.profileConflictId}>
                          <StyledPill
                            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-conflict-name`}
                            text={conflict.note}
                            id={`conflict${conflict.profileConflictId}`}
                            rightIcons={conflict.note ? ['document'] : null}
                          />
                          <StyledUncontrolledTooltip
                            data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-conflict-tooltip`}
                            placement="top"
                            autohide={false}
                            target={`conflict${conflict.profileConflictId}`}
                            data-testid={`conflict-tooltip-${conflict.profileConflictId}`}
                          >
                            <DuoLink
                              data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-conflict-details`}
                              href={`/profile/${profile.profileId}/conflicts`}
                            >
                              {t('common:profile.viewConflictDetails')}
                            </DuoLink>
                          </StyledUncontrolledTooltip>
                        </Fragment>
                      ))}
                    </StyledColValue>
                    <StyledColEditLink sm="2" xs="3">
                      <DuoLink
                        data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-other-conflicts-edit`}
                        href={`/profile/${profile.profileId}/conflicts`}
                      >
                        {t(buttonEditI18Label)}
                      </DuoLink>
                    </StyledColEditLink>
                  </>
                </StyledRow>
              )}
              <StyledRow data-testid="internal-note">
                <StyledColLbl
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-internal-note`}
                  sm="3"
                  xs="12"
                >
                  {t('common:profile.internalNote')}:
                </StyledColLbl>
                <StyledColValue
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-internal-note`}
                  sm="7"
                  xs="9"
                  faded={!hasNotes}
                >
                  {hasNotes ? renderInternalNote(profile.notes) : t(commonNoSectionI18Label)}
                </StyledColValue>
                <StyledColEditLink sm="2" xs="3">
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-internal-note-edit`}
                    href={`/profile/${profile.profileId}/internal-note`}
                  >
                    {t(buttonEditI18Label)}
                  </DuoLink>
                </StyledColEditLink>
              </StyledRow>
              <StyledRow data-testid="bookout-dates">
                <StyledColLbl
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-bookout-dates`}
                  sm="3"
                  xs="12"
                >
                  {t('common:profile.bookoutDates')}:
                </StyledColLbl>
                <StyledColValue
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-bookout-dates`}
                  sm="7"
                  xs="9"
                  faded={!hasBookOuts}
                >
                  {hasBookOuts
                    ? _map(
                        _orderBy(activeBookOuts, ['startDate', ({ endDate }) => endDate || ''], ['asc', 'asc']),
                        (bookOut) => (
                          <div key={bookOut.profileBookoutId} data-testid="book-out-date-item">
                            <StyledBreakString>
                              {bookOut.startDate && getReadableDate(bookOut.startDate)}
                              {bookOut.startDate && bookOut.endDate && ` - ${getReadableDate(bookOut.endDate)}`}
                            </StyledBreakString>
                          </div>
                        )
                      )
                    : t(commonNoSectionI18Label)}
                </StyledColValue>
                <StyledColEditLink sm="2" xs="3">
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-bookout-dates-edit`}
                    href={`/profile/${profile.profileId}/book-out-dates`}
                  >
                    {t(buttonEditI18Label)}
                  </DuoLink>
                </StyledColEditLink>
              </StyledRow>
              <StyledRow data-testid="work-permit-dates">
                <StyledColLbl
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-heading-work-permit-expiration-date`}
                  sm="3"
                  xs="12"
                >
                  {t('common:profile.workPermitExpirationDate')}:
                </StyledColLbl>
                <StyledColValue
                  data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-work-permit-expiration-date`}
                  sm="7"
                  xs="9"
                  faded={!profile.workPermitExp}
                >
                  {profile.workPermitExp ? getReadableDate(profile.workPermitExp) : t(commonNoSectionI18Label)}
                </StyledColValue>
                <StyledColEditLink sm="2" xs="3">
                  <DuoLink
                    data-qa-id={`${SCREEN_NAMES.PROFILE_VIEW}-${sectionName}-work-permit-expiration-date-edit`}
                    href={`/profile/${profile.profileId}/work-permit-expiration-date`}
                  >
                    {t(buttonEditI18Label)}
                  </DuoLink>
                </StyledColEditLink>
              </StyledRow>
            </>
          )}
        </RecordsContainer>
      </DetailsContainer>
    </DetailsWrapper>
  );
};

export default Details;
